import { useParams } from "react-router-dom";
import { metaLabelClass } from "@groot/ui/typography";
import { RequestStatusBadge } from "@/components/status-badges";
import {
  usePhotoRequest,
  useUpdatePhotoRequest,
  useDeletePhoto,
  useAddPhoto,
  useClearPhotos,
  useDownloadPhotoRequestZip,
  useFindDuplicates,
  MAX_PHOTOS_PER_REQUEST,
  type DuplicateResult,
} from "@/pages/photo-request/hooks/usePhotoRequests";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  Trash2,
  Upload,
  Loader2,
  Eraser,
  ShieldCheck,
  Download,
  Search,
  SearchX,
} from "lucide-react";
import { Skeleton } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@groot/ui/dialog";
import { Textarea } from "@groot/ui/textarea";
import { Label } from "@groot/ui/label";
import { useUploadFile } from "@groot/shell/pages/storage/hooks/useStorage";
import { useRef, useState } from "react";
import { PrintSheetsCard } from "@/pages/print-batch/components/PrintSheetsCard";

export function PhotoRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const requestId = parseInt(id || "0", 10);
  const { data: request, isLoading } = usePhotoRequest(requestId);
  const updatePhotoRequest = useUpdatePhotoRequest();
  const deletePhoto = useDeletePhoto();
  const addPhoto = useAddPhoto();
  const clearPhotos = useClearPhotos();
  const downloadZip = useDownloadPhotoRequestZip();
  const findDuplicates = useFindDuplicates();
  const uploadFile = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearNote, setClearNote] = useState("");
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateResult | null>(null);

  const handleFiles = async (files: FileList | File[]) => {
    if (!files.length || !request) return;

    const fileArray = Array.from(files);
    const currentCount = request.files?.length ?? 0;
    const remaining = MAX_PHOTOS_PER_REQUEST - currentCount;

    if (remaining <= 0) {
      toast.error("Photo limit reached", {
        description: `This request already has the maximum of ${MAX_PHOTOS_PER_REQUEST} photos.`,
      });
      return;
    }

    const toUpload = fileArray.length <= remaining ? fileArray : fileArray.slice(0, remaining);
    if (toUpload.length < fileArray.length) {
      toast.warning("Photo limit", {
        description: `Only ${toUpload.length} of ${fileArray.length} photo(s) uploaded — the ${MAX_PHOTOS_PER_REQUEST}-photo limit has been reached.`,
      });
    }

    setIsUploading(true);
    try {
      for (const file of toUpload) {
        const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const uploadResult = await uploadFile.mutateAsync({
          file,
          filePath: `photo-requests/${request.id}/${Date.now()}-${sanitized}`,
          contentType: file.type,
        });

        await addPhoto.mutateAsync({
          photoRequestId: request.id,
          fileName: file.name,
          fileUrl: uploadResult.filePath,
        });
      }
      toast.success("Uploaded", { description: "Photos uploaded successfully" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Failed to upload photos" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    return (
      <PageLayout title="Photo Request" description="Loading...">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!request) {
    return (
      <PageLayout title="Photo Request" description="Not found">
        <EmptyState
          icon={SearchX}
          title="Photo request not found"
          description="This request may have been deleted or the link is invalid."
          action={
            <Button variant="outline" asChild>
              <Link to="/photo-requests">
                <ArrowLeft className="size-4" />
                Back to Requests
              </Link>
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const copyPublicLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/public/photo-requests/${request.publicLink}`,
    );
    toast.success("Copied", { description: "Public link copied to clipboard" });
  };

  const completeRequest = async () => {
    try {
      await updatePhotoRequest.mutateAsync({
        id: requestId,
        data: { status: "completed" },
      });
      toast.success("Success", { description: "Photo request completed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to complete photo request" });
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await downloadZip.mutateAsync(requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `riqah-design-photo-request-${request.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to download photos" });
    }
  };

  const handleFindDuplicates = async () => {
    try {
      const result = await findDuplicates.mutateAsync(requestId);
      setDuplicateResult(result);
      setDuplicateDialogOpen(true);
      if (result.duplicateCount === 0) {
        toast.success("No duplicates found", {
          description: `Scanned ${result.scannedCount} photo(s) — all unique.`,
        });
      } else {
        toast.success("Duplicates found", {
          description: `${result.duplicateCount} duplicate photo(s) across ${result.groups.length} group(s).`,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to scan for duplicates" });
    }
  };

  const removeDuplicate = async (fileId: number) => {
    try {
      await deletePhoto.mutateAsync({ photoRequestId: requestId, fileId });
      // Refresh the duplicate results so the deleted file disappears.
      if (duplicateResult) {
        const updated: DuplicateResult = {
          ...duplicateResult,
          groups: duplicateResult.groups
            .map((g) => ({
              ...g,
              duplicates: g.duplicates.filter((d) => d.id !== fileId),
            }))
            .filter((g) => g.duplicates.length > 0),
        };
        updated.duplicateCount = updated.groups.reduce((sum, g) => sum + g.duplicates.length, 0);
        setDuplicateResult(updated);
      }
      toast.success("Deleted", { description: "Duplicate photo removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete photo" });
    }
  };

  const removePhoto = async (fileId: number) => {
    try {
      await deletePhoto.mutateAsync({ photoRequestId: requestId, fileId });
      toast.success("Deleted", { description: "Photo removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete photo" });
    }
  };

  const openClearDialog = () => {
    setClearNote("Photos cleared after printing completed.");
    setClearDialogOpen(true);
  };

  const handleClearPhotos = async () => {
    try {
      await clearPhotos.mutateAsync({ photoRequestId: requestId, note: clearNote });
      toast.success("Photos cleared", {
        description: "All photos have been permanently deleted for privacy.",
      });
      setClearDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to clear photos" });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(e.target.files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <PageLayout
      title={`Photo Request #${request.id}`}
      description={`Order ${request.order?.orderNumber}`}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyPublicLink}>
            <Copy className="size-4" />
            Copy Link
          </Button>
          {!request.photosClearedAt && (request.files?.length ?? 0) > 0 && (
            <Button
              variant="outline"
              onClick={handleFindDuplicates}
              disabled={findDuplicates.isPending}
            >
              {findDuplicates.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
              {findDuplicates.isPending ? "Scanning…" : "Find Duplicates"}
            </Button>
          )}
          {!request.photosClearedAt && (request.files?.length ?? 0) > 0 && (
            <Button variant="outline" onClick={handleDownloadZip} disabled={downloadZip.isPending}>
              {downloadZip.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              {downloadZip.isPending ? "Downloading…" : "Download"}
            </Button>
          )}
          {request.status === "pending" && (
            <Button onClick={completeRequest} disabled={updatePhotoRequest.isPending}>
              <CheckCircle className="size-4" />
              Complete
            </Button>
          )}
          {!request.photosClearedAt && (request.files?.length ?? 0) > 0 && (
            <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={openClearDialog}>
                  <Eraser className="size-4" />
                  Clear All Photos
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Clear all photos?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    This permanently deletes all {request.files?.length} photo(s) from storage for
                    privacy once printing is done. This cannot be undone.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="clear-note">Audit note</Label>
                    <Textarea
                      id="clear-note"
                      value={clearNote}
                      onChange={(e) => setClearNote(e.target.value)}
                      placeholder="e.g. Photos cleared after printing completed"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      This note is saved on the request so a future audit can confirm the photos
                      were intentionally cleared.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setClearDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleClearPhotos}
                    disabled={clearPhotos.isPending || !clearNote.trim()}
                  >
                    {clearPhotos.isPending ? "Clearing…" : "Clear all photos"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Button variant="outline" asChild>
            <Link to="/photo-requests">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Request Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
              <div>
                <p className={metaLabelClass}>Status</p>
                <div className="mt-1">
                  <RequestStatusBadge status={request.status} />
                </div>
              </div>
              <div>
                <p className={metaLabelClass}>Order</p>
                <p className="mt-1 text-sm text-foreground">
                  <Link
                    to={`/orders/${request.orderId}`}
                    className="font-medium hover:text-primary"
                  >
                    {request.order?.orderNumber}
                  </Link>
                </p>
              </div>
              <div>
                <p className={metaLabelClass}>Customer</p>
                <p className="mt-1 text-sm text-foreground">
                  <Link
                    to={`/customers/${request.customerId}`}
                    className="font-medium hover:text-primary"
                  >
                    {request.customer?.name}
                  </Link>
                </p>
                <p className="text-xs text-muted-foreground">{request.customer?.mobileNumber}</p>
              </div>
              <div>
                <p className={metaLabelClass}>Created</p>
                <p className="mt-1 text-sm text-foreground">
                  {new Date(request.createdAt).toLocaleDateString()}
                </p>
              </div>
              {request.completedAt && (
                <div>
                  <p className={metaLabelClass}>Completed</p>
                  <p className="mt-1 text-sm text-foreground">
                    {new Date(request.completedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <PrintSheetsCard request={request} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Photos
              {(request.files?.length ?? 0) > 0 && (
                <Badge variant="secondary">
                  {request.files?.length} / {MAX_PHOTOS_PER_REQUEST}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {request.photosClearedAt && (
              <div className="rounded-lg border border-success/40 bg-success/10 p-4 flex gap-3">
                <ShieldCheck className="size-5 text-success shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-success">Photos cleared for privacy</p>
                  <p className="text-xs text-success">
                    On {new Date(request.photosClearedAt).toLocaleString()},{" "}
                    {request.photosClearedCount ?? 0} photo(s) were permanently deleted after
                    printing was completed.
                  </p>
                  {request.photosClearedNote && (
                    <p className="text-xs text-success italic">
                      Audit note: “{request.photosClearedNote}”
                    </p>
                  )}
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {!request.photosClearedAt && (
              <>
                {/* Drop zone */}
                <button
                  type="button"
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  disabled={isUploading}
                  className={`w-full border-2 border-dashed rounded-xl py-10 flex flex-col items-center gap-2 disabled:pointer-events-none ${
                    isDragOver
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-foreground/30 hover:bg-muted/50"
                  }`}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="size-8 text-muted-foreground animate-spin" />
                      <span className="text-sm font-medium text-foreground">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="size-8 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        Drop photos here or click to upload
                      </span>
                      <span className="text-xs text-muted-foreground">
                        JPG, PNG, HEIC · up to {MAX_PHOTOS_PER_REQUEST} photos
                      </span>
                    </>
                  )}
                </button>

                {/* Photo grid */}
                {request.files && request.files.length > 0 && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {request.files.map((file) => (
                      <div
                        key={file.id}
                        className="border border-border rounded-lg overflow-hidden group"
                      >
                        <div className="aspect-square bg-muted flex items-center justify-center relative">
                          <img
                            src={`/api/v1/photo-requests/${requestId}/files/${file.id}`}
                            alt={file.fileName}
                            className="w-full h-full object-cover"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 size-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removePhoto(file.id)}
                            disabled={deletePhoto.isPending}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium truncate">{file.fileName}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {request.files?.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center">
                    No photos yet. Upload above or share the{" "}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={copyPublicLink}
                    >
                      public link
                    </button>{" "}
                    with the customer.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Duplicate detection results */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Duplicate Photos</DialogTitle>
          </DialogHeader>
          {duplicateResult && duplicateResult.duplicateCount === 0 && (
            <p className="text-sm text-muted-foreground">
              No duplicates found. Scanned {duplicateResult.scannedCount} photo(s) — all unique.
            </p>
          )}
          {duplicateResult && duplicateResult.duplicateCount > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Found <span className="font-medium">{duplicateResult.duplicateCount}</span>{" "}
                duplicate photo(s) in {duplicateResult.groups.length} group(s). The original (kept)
                is shown first; delete the redundant copies below.
              </p>
              {duplicateResult.groups.map((group, gi) => (
                <div key={gi} className="border border-border rounded-lg p-3 space-y-2">
                  <p className={metaLabelClass}>
                    Group {gi + 1} · {group.duplicates.length + 1} identical ·{" "}
                    {group.hammingDistance === 0
                      ? "exact match"
                      : `${group.hammingDistance} bits apart`}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {/* Original — marked as kept */}
                    <div className="relative w-28 shrink-0">
                      <div className="aspect-square rounded-lg overflow-hidden border-2 border-success/40">
                        <img
                          src={`/api/v1/photo-requests/${requestId}/files/${group.original.id}`}
                          alt={group.original.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="absolute -top-2 -right-2 bg-success text-success-foreground text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                        Keep
                      </span>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {group.original.fileName}
                      </p>
                    </div>
                    {/* Duplicates — deletable */}
                    {group.duplicates.map((dup) => (
                      <div key={dup.id} className="relative w-28 shrink-0">
                        <div className="aspect-square rounded-lg overflow-hidden border border-border">
                          <img
                            src={`/api/v1/photo-requests/${requestId}/files/${dup.id}`}
                            alt={dup.fileName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="mt-1 w-full h-7 text-xs"
                          onClick={() => removeDuplicate(dup.id)}
                          disabled={deletePhoto.isPending}
                        >
                          <Trash2 className="size-3" />
                          Delete
                        </Button>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {dup.fileName}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
